import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiRoute, FeatureFlag, Route } from 'config';
import { get, patch } from 'utils/network';
import { postMessage, WebViewMessage } from 'utils/webview';
import useCurrentUser from 'hooks/useCurrentUser';
import useAsyncEffect from 'hooks/useAsyncEffect';

import Button from 'react-bootstrap/Button';
import RadioButton from 'components/shared/RadioButton';
import Checkbox from 'components/shared/Checkbox';

import { getOptionsforCancel } from 'models/Subscription';
import Plan from 'models/Plan';
import IPlanInfo from 'models/IPlanInfo';

import { Component as CircleErrorIcon } from 'icons/icon-circle--error.svg';
import { Component as CaretIcon } from 'icons/icon-ui--caret-outline.svg';
import CancelModalContainer from 'platform/components/cancellation/CancelModalContainer';
import { useFlags } from '@flagsmith/flagsmith/react';
import FlexBoxCancelRebuttalModal from './modals/FlexBoxCancelRebuttalModal';
import UpdatePlan from '../../UpdatePlan';

const cancelOptions = getOptionsforCancel();

type Props = {
  handleErrors: () => void;
};

function CancelSubscription({ handleErrors }: Props): ReactElement {
  const navigate = useNavigate();
  const {
    currentUser,
    reload,
  } = useCurrentUser();

  const flags = useFlags(['modal_cancellation_flow']);
  const cancellationFlag = flags.modal_cancellation_flow;
  const isModalCancellationEnabled = cancellationFlag?.enabled && cancellationFlag?.value === 'show';
  // eslint-disable-next-line max-len
  const isDiversifiedPlansEnabled = currentUser.hasFeatureEnabled(FeatureFlag.DIVERSIFIED_PLANS);

  const {
    isCancelled,
    subscription,
    canEditPlan,
  } = currentUser;
  const plan: Plan = new Plan(subscription?.plan, subscription?.extras,
    subscription?.is_diversified);

  const [showCancelOptions, setShowCancelOptions] = useState(false);
  const [showModalCancellation, setShowModalCancellation] = useState(false);
  const [cancelReasonDetail, setCancelReasonDetail] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [preparedFollowUp, setPreparedFollowUp] = useState(false);
  const [showCancelRebuttal, setShowCancelRebuttal] = useState(false);
  const [isPlanInitialized, setPlanInitialized] = useState(Plan.isInitialized);
  const [showUpdatePlanRebuttal, setShowUpdatePlanRebuttal] = useState(false);

  const reasonsList = [
    'dietary',
    'recipes',
    'less_meals',
    'cooking_time',
    'my_own',
    'not_kid_friendly',
    'need_variety',
    'portions_too_small',
    'flavor_taste',
  ];

  const updatePlanRebuttalList = [
    'dietary',
    'recipes',
    'flavor_taste',
  ];

  const triggerShowPauseOptions = () => {
    navigate(Route.MANAGE_SUBSCRIPTION);
  };

  const handleCancelShow = () => {
    if (isModalCancellationEnabled) {
      setShowModalCancellation(true);
    } else {
      setShowCancelOptions(!showCancelOptions);
    }
  };

  const onCancelReasonChange = (e) => setCancelReason(e.target.id);

  const handleDeactivation = async (deactivationType) => {
    setShowCancelRebuttal(false);
    setShowUpdatePlanRebuttal(false);

    const payload = {
      subscription: {
        status: deactivationType,
        subscription_cancellation_attributes: {
          reason: cancelReason,
          reason_details: cancelReasonDetail,
          prepared_follow_up_opt_in: preparedFollowUp,
        },
      },
    };

    try {
      const response = await patch(`${ApiRoute.DEACTIVATE}`, payload);

      if (response.ok) {
        postMessage(WebViewMessage.SUBSCRIPTION_CANCELLED);

        reload();
        // this is to support the tanstack setup in the new platform
        navigate(Route.UPCOMING_MENUS, {
          state: {
            invalidate: true,
          },
        });
      }
    } catch (e) {
      handleErrors();
      window.scrollTo(0, 0);
    }
  };

  const validReasonToShowRebuttal = () => {
    if (reasonsList.includes(cancelReason)) {
      return true;
    }
    return false;
  };

  const handleCancelTrigger = () => {
    if (updatePlanRebuttalList.includes(cancelReason) && canEditPlan && isDiversifiedPlansEnabled) {
      setShowUpdatePlanRebuttal(true);
    } else if (validReasonToShowRebuttal()) {
      setShowCancelRebuttal(true);
    } else {
      handleDeactivation('cancelled');
    }
  };

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
      Plan.initialize(response.parsedBody || []);
      if (isMounted) setPlanInitialized(true);
    }
  }, []);

  return (
    <div
      className={[
        'c-subscription-management-option',
        'c-pause-subscription-form',
        isCancelled ? 'is-disabled' : '',
        showCancelOptions ? 'is-active' : '',
      ].join(' ')}
    >
      <div className="c-subscription-management-option__content">
        <div
          className={[
            'u-text',
            'u-text--center',
            'c-subscription-management-option__header',
            isCancelled ? 'disabled' : '',
          ].join(' ')}
          role="button"
          aria-controls="cancel-subscription-content"
          tabIndex={0}
          onClick={handleCancelShow}
          onKeyPress={(e) => e.key === 'Enter'}
        >
          <div className="c-subscription-management-option__icon">
            <CircleErrorIcon
              height="36"
              width="36"
              className="c-svg-fill-gray-068-color"
            />
          </div>
          <h3 className="c-heading c-heading--brand-7">
            CANCEL YOUR SUBSCRIPTION
          </h3>
          <p className="u-text u-text--lg">
            Select this option to discontinue your Purple Carrot subscription.
          </p>
          <div className="d-inline c-subscription-management-option__arrow">
            <CaretIcon height="13" width="13" className="c-svg-fill-primary-color" />
          </div>
        </div>

        <div
          id="cancel-subscription-content"
          role="region"
          className={[
            'c-subscription-management-option__form',
            'collapse',
            (showCancelOptions && !isCancelled) ? 'show is-active' : '',
          ].join(' ')}
        >
          <hr />
          <p className="u-text u-text--lg">
            Please select a reason below to cancel your subscription.
          </p>
          <form onSubmit={(e) => e.preventDefault()}>
            <fieldset className="c-form-group">
              <div
                className="c-input-radio-group"
                aria-label="Cancel Subscription Reason"
              >
                {cancelOptions.map((option) => (
                  <RadioButton
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    name="reason"
                    onChange={onCancelReasonChange}
                  />
                ))}
              </div>
            </fieldset>
          </form>
          <div className="c-form-group c-subscription-management-option__more">
            <div className="c-form-group text required">
              <label className="text required">
                Please tell us more
                <sup>*</sup>

                <span>
                  <sup>*</sup>
                  {' '}
                  Required
                </span>
                <textarea
                  className="text required"
                  rows={4}
                  cols={60}
                  value={cancelReasonDetail}
                  onChange={(e) => setCancelReasonDetail(e.target.value)}
                />
              </label>
            </div>
          </div>
          {subscription?.plan === 'prepared_one_serving' && (
            <div className="c-form-group">
              <Checkbox
                id="prepared-follow-up-input"
                label={(
                  <>
                    If you&apos;d be willing to talk with us
                    more about Purple Carrot Prepared Meals,
                    we&apos;d be so thankful! If so,
                    please let us know by clicking the checkbox.
                  </>
                )}
                checked={preparedFollowUp}
                data-cy="manage-subscription-prepared-follow-up-input"
                disabled={false}
                onChange={(e) => setPreparedFollowUp(e.target.checked)}
              />
            </div>
          )}
          <Button
            className="d-block c-button c-button--primary"
            onClick={handleCancelTrigger}
            disabled={cancelReason.length === 0 || cancelReasonDetail.length === 0}
            data-cy="manage-subscription-cancel-subscription"
          >
            Cancel Subscription
          </Button>
        </div>
      </div>

      {showCancelRebuttal && (
        <FlexBoxCancelRebuttalModal
          show={showCancelRebuttal}
          reason={cancelReason}
          onClose={() => setShowCancelRebuttal(false)}
          showPauseSubscription={triggerShowPauseOptions}
          triggerDeactivation={handleDeactivation}
        />
      )}

      {isPlanInitialized && showUpdatePlanRebuttal && (
        <UpdatePlan
          show={showUpdatePlanRebuttal}
          existingPlan={plan}
          onClose={() => setShowUpdatePlanRebuttal(false)}
          showSubscriptionCancelTrigger
          onSubscriptionCancel={() => handleDeactivation('cancelled')}
        />
      )}
      {showModalCancellation && (
        <CancelModalContainer
          show={showModalCancellation}
          onClose={() => setShowModalCancellation(false)}
          handleErrors={handleErrors}
        />
      )}
    </div>
  );
}

export default CancelSubscription;
